
export interface ProposalData {
  clientName: string; projectTitle: string; serviceType: string;
  rateType: 'hourly' | 'fixed'; hourlyRate: number; hours: number; buffer: number;
  fixedPrice: number; startDate: string; deadline: string;
  scope: string; outOfScope: string; approach: string;
  includeRushFee: boolean; includeKillFee: boolean; revisionRounds: number;
}

export const SERVICE_TEMPLATES: Record<string, { phases: { name: string; activities: string[] }[]; deliverables: string[]; defaultTerms: string[] }> = {
  'web_dev': {
    phases: [
      { name: 'Discovery', activities: ['Stakeholder interviews', 'Requirements documentation', 'Technical architecture planning'] },
      { name: 'Design', activities: ['Wireframes and mockups', 'UI/UX design', 'Design system documentation'] },
      { name: 'Development', activities: ['Frontend development', 'Backend development', 'API integration', 'Database design'] },
      { name: 'Testing', activities: ['QA testing', 'User acceptance testing', 'Bug fixes'] },
      { name: 'Deployment', activities: ['Production deployment', 'Documentation handover', 'Training session'] }
    ],
    deliverables: ['Requirements Document', 'Design Mockups', 'Source Code Repository', 'Test Report', 'User Manual'],
    defaultTerms: ['Hosting costs not included', 'Third-party API fees not included', 'Content creation not included']
  },
  'consulting': {
    phases: [
      { name: 'Assessment', activities: ['Current state analysis', 'Stakeholder interviews', 'Data collection'] },
      { name: 'Analysis', activities: ['Findings synthesis', 'Benchmarking', 'Gap analysis'] },
      { name: 'Recommendations', activities: ['Strategy development', 'Implementation roadmap', 'Resource planning'] },
      { name: 'Presentation', activities: ['Executive presentation', 'Detailed report delivery', 'Q&A session'] }
    ],
    deliverables: ['Assessment Report', 'Analysis Summary', 'Strategic Roadmap', 'Final Presentation Deck'],
    defaultTerms: ['Travel expenses reimbursed with receipts', 'Data access required within 48 hours']
  },
  'design': {
    phases: [
      { name: 'Discovery', activities: ['Brand audit', 'Competitor analysis', 'Mood boards'] },
      { name: 'Concept', activities: ['3 initial design concepts', 'Presentation to client', 'Feedback collection'] },
      { name: 'Refinement', activities: ['Up to N rounds of revisions', 'Selected concept development', 'Asset production'] },
      { name: 'Finalization', activities: ['Production-ready files', 'Usage guidelines', 'Asset package'] }
    ],
    deliverables: ['Primary Logo Files (AI, EPS, PNG, SVG)', 'Color Palette Specification', 'Typography Guidelines', 'Brand Style Guide', 'Social Media Templates'],
    defaultTerms: ['Additional revisions beyond included rounds billed hourly', 'Stock photography not included']
  }
};

export const SCOPE_CREEP_PATTERNS = [
  { pattern: /\b(just|quickly|small|minor|tiny)\s+(?:add|change|update|fix)\b/gi, risk: 'high', message: "'Just a small change' often expands scope. Define exactly what's included." },
  { pattern: /\b(unlimited|endless|as many as needed|no limit)\b/gi, risk: 'critical', message: 'UNLIMITED revisions = scope creep guarantee. Cap at specific number.' },
  { pattern: /\b(and anything else|etcetera|etc\.|and so on|whatever else)\b/gi, risk: 'critical', message: 'Vague scope boundaries. List explicit exclusions.' },
  { pattern: /\b(ASAP|urgent|rush|emergency)\b/gi, risk: 'medium', message: 'Rush requests need rush fees or timeline adjustments.' },
  { pattern: /\b(final approval|until satisfied|until perfect)\b/gi, risk: 'high', message: "'Until satisfied' has no endpoint. Define acceptance criteria." },
];

export function detectScopeCreep(text: string) { return SCOPE_CREEP_PATTERNS.filter(r => r.pattern.test(text)).map(r => ({ ...r, matches: text.match(r.pattern) || [] })); }

export function calculateRate(data: ProposalData) {
  if (data.rateType === 'hourly') {
    const base = data.hours * data.hourlyRate;
    const buffer = base * (data.buffer / 100);
    return { base, buffer, total: base + buffer, breakdown: { hours: data.hours, rate: data.hourlyRate, bufferPercent: data.buffer } };
  }
  return { base: data.fixedPrice, buffer: 0, total: data.fixedPrice, breakdown: { type: 'fixed' } };
}

export function generateProposalMarkdown(data: ProposalData): string {
  const tmpl = SERVICE_TEMPLATES[data.serviceType] || SERVICE_TEMPLATES.web_dev;
  const calc = calculateRate(data);
  const milestones = data.rateType === 'hourly'
    ? `| Milestone | Deliverable | Payment |\n|-----------|-------------|---------|\n| Kickoff | Project initiation | 25% ($${(calc.total * 0.25).toFixed(0)}) |\n| Mid-point | Phase completion | 25% ($${(calc.total * 0.25).toFixed(0)}) |\n| Development complete | All features delivered | 25% ($${(calc.total * 0.25).toFixed(0)}) |\n| Launch | Go-live | 25% ($${(calc.total * 0.25).toFixed(0)}) |`
    : `| Milestone | Deliverable | Payment |\n|-----------|-------------|---------|\n| Deposit | Contract signing | 50% ($${(calc.total * 0.5).toFixed(0)}) |\n| Final | Project completion | 50% ($${(calc.total * 0.5).toFixed(0)}) |`;

  return `# Project Proposal: ${data.projectTitle}

**Prepared for:** ${data.clientName}  
**Prepared by:** [Your Name] — CatalystAgile  
**Date:** ${new Date().toLocaleDateString()}  
**Project Type:** ${data.serviceType.replace('_', ' ')}

---

## 1. Scope of Work

${data.scope || '[Describe the project scope here]'}

### Phases

${tmpl.phases.map((p, i) => `${i+1}. **${p.name}**\n${p.activities.map(a => `   - ${a}`).join('\n')}`).join('\n\n')}

---

## 2. Deliverables

${tmpl.deliverables.map(d => `- [ ] ${d}`).join('\n')}

---

## 3. Timeline

- **Start Date:** ${data.startDate || 'TBD'}
- **Deadline:** ${data.deadline || 'TBD'}
- **Duration:** ${data.startDate && data.deadline ? calculateWeeks(data.startDate, data.deadline) + ' weeks' : 'TBD'}

---

## 4. Investment

**Total Project Investment: $${calc.total.toFixed(2)}**

${data.rateType === 'hourly' ? `- Estimated Hours: ${data.hours}\n- Hourly Rate: $${data.hourlyRate}\n- Buffer (${data.buffer}%): $${calc.buffer.toFixed(2)}\n- **Total: $${calc.total.toFixed(2)}**` : `- Fixed Price: $${data.fixedPrice}`}

### Payment Schedule

${milestones}

${data.includeRushFee ? '- **Rush Fee:** 1.5x rate for turnaround under 48 hours\n' : ''}${data.includeKillFee ? '- **Kill Fee:** 50% of remaining contract value if cancelled after work begins\n' : ''}

---

## 5. Out of Scope

The following are explicitly NOT included:

${data.outOfScope ? data.outOfScope.split('\n').map(s => `- ${s.trim()}`).join('\n') : tmpl.defaultTerms.map(t => `- ${t}`).join('\n')}

---

## 6. Terms & Conditions

- **Revisions:** Up to ${data.revisionRounds} rounds of revisions per deliverable. Additional revisions billed at hourly rate.
- **Communication:** Primary contact response within 24 business hours.
- **Intellectual Property:** Custom deliverables transfer to client upon final payment. Pre-existing tools and methodologies remain consultant property.
- **Confidentiality:** Both parties agree to standard NDA terms.
- **Termination:** Either party may terminate with 14 days written notice. Client pays for all work completed to date${data.includeKillFee ? ' plus kill fee' : ''}.
- **Limitation of Liability:** Consultant liability limited to total project value.
- **Governing Law:** [State/Province], [Country].

---

*This proposal is valid for 30 days from the date above.*
`;
}

function calculateWeeks(start: string, end: string) {
  const s = new Date(start), e = new Date(end);
  return Math.max(1, Math.ceil((e.getTime() - s.getTime()) / (1000 * 60 * 60 * 24 * 7)));
}
